import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import "../../Assets/Styles/pages/landingRedesign.scss";
import "../../Assets/Styles/pages/blogDetailPageRedesign.scss";
import Nav from "../../Components/LandingComponents/Nav";
import { getBlogBySlug } from "../../actions/Blogs/blogsActions";
import { BLOG_POSTS, type BlogPost } from "../../Config/blogPosts";
import usePageMetadata from "../../Utils/usePageMetadata";

interface GhostPost {
  title: string;
  html: string;
  excerpt?: string;
  feature_image?: string;
  published_at?: string;
}

// Same confirmed shape as the list page (blogs.tsx) -- `GET {REACT_APP_API_URL}blogs/:idOrSlug`.
interface ApiPost {
  title: string;
  content: string;
  summary?: string;
  coverImage?: string;
  createdAt?: string;
}

function useReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setPct(total > 0 ? Math.min(100, Math.max(0, (h.scrollTop / total) * 100)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return pct;
}

const BlogDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const pct = useReadingProgress();

  // Source priority (same as blogs.tsx): our own Blogs API first, then Ghost CMS, then the
  // local static post matching this slug.
  const [apiPost, setApiPost] = useState<ApiPost | null | undefined>(undefined);
  const [ghostPost, setGhostPost] = useState<GhostPost | null | undefined>(undefined);
  // The iframe reports its own load event before its content is actually measured/sized (its
  // `onLoad` handler below does the measuring) -- true only once `updateHeight()` has set a
  // real pixel height, so the skeleton stays up for exactly the collapsed-box window the user
  // was seeing, not just until the iframe DOM exists.
  const [apiFrameReady, setApiFrameReady] = useState(false);
  const staticPost = BLOG_POSTS.find((p) => p.slug === slug);
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  usePageMetadata(
    apiPost
      ? `${apiPost.title} - eZy WaterMark`
      : ghostPost
        ? `${ghostPost.title} - eZy WaterMark`
        : staticPost
          ? `${staticPost.title} - eZy WaterMark`
          : "Blogs - eZy WaterMark",
    apiPost?.summary ?? ghostPost?.excerpt ?? staticPost?.excerpt
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;

    const fetchApiPost = async (): Promise<boolean> => {
      if (!apiBaseUrl || !slug) return false;
      try {
        const base = apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`;
        const response = await fetch(`${base}blogs/${encodeURIComponent(slug)}`);
        if (!response.ok) return false;
        const post = (await response.json()) as ApiPost;
        if (active && post?.title && post?.content) {
          setApiPost(post);
          return true;
        }
        return false;
      } catch (error) {
        console.error("[BlogsAPI] Error fetching post:", error);
        return false;
      }
    };

    const fetchGhostPost = async () => {
      const response = (await getBlogBySlug(slug ?? "")()) as GhostPost | null;
      if (active) setGhostPost(response);
    };

    void (async () => {
      const foundInApi = await fetchApiPost();
      if (!foundInApi && active) {
        setApiPost(null);
        void fetchGhostPost();
      }
    })();

    return () => {
      active = false;
    };
  }, [slug, apiBaseUrl]);

  if (apiPost === undefined) {
    // Still resolving the Blogs API lookup -- avoid a flash of "not found"/stale content while
    // in flight.
    return <BlogDetailSkeleton />;
  }

  if (apiPost) {
    return (
      <div className="landing-redesign">
        <Nav />
        <ProgressBar pct={pct} />
        <section className="bd-hero bd-hero--compact lr-grain">
          <div className="bd-hero__orbs">
            <div className="bd-hero__orb bd-hero__orb--1" />
            <div className="bd-hero__orb bd-hero__orb--2" />
          </div>
          <div className="bd-hero__content">
            <button type="button" className="bd-hero__back" onClick={() => history.push("/blogs")}>
              ← Back to blogs
            </button>
            <span className="bd-hero__badge">
              <span />
              The eZy Journal
            </span>
          </div>
        </section>
        <section className="bd-article bd-article--api">
          {/* Blogs API `content` is a FULL standalone HTML document (own <style>, generic class
              names like `.hero-card`/`.step-title`) -- unlike Ghost's `html`, which is a bare
              fragment safe to inline. Rendering it via `dangerouslySetInnerHTML` would leak that
              document's stylesheet onto this page and risk colliding with the site's own CSS
              (or the site's CSS overriding it). An iframe keeps it fully isolated; height is set
              from the loaded document with transparency and scrolling hidden. */}
          {/* Reserves real content-sized space (min-height in CSS) and shows a shimmer while the
              iframe above is still un-measured, instead of the collapsed ~150px default-iframe
              box the user briefly saw before `onLoad` sets a real pixel height below. */}
          {!apiFrameReady && (
            <div className="bd-article__api-skeleton" aria-hidden="true">
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "60%", height: "1.6em" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "95%", marginTop: "1.25em" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "90%" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "97%" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "40%" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "85%", marginTop: "1.25em" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "92%" }} />
              <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "60%" }} />
            </div>
          )}
          <iframe
            title={apiPost.title}
            srcDoc={apiPost.content}
            className="bd-article__api-frame"
            scrolling="no"
            style={{
              width: "100%",
              border: "none",
              display: "block",
              background: "transparent",
              overflow: "hidden",
              // Zero height (not just `visibility:hidden`) until measured, so this cannot itself
              // be the collapsed box the skeleton above is standing in for.
              height: apiFrameReady ? undefined : 0,
            }}
            onLoad={(e) => {
              const frame = e.currentTarget;
              try {
                const doc = frame.contentDocument;
                if (!doc) {
                  setApiFrameReady(true);
                  return;
                }

                // 1. Force transparent background and hide any internal scrollbars
                const styleEl = doc.createElement("style");
                styleEl.textContent = `
                  html, body {
                    background: transparent !important;
                    background-color: transparent !important;
                    overflow: hidden !important;
                    overflow-y: hidden !important;
                    scrollbar-width: none !important;
                    -ms-overflow-style: none !important;
                  }
                  ::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                  }
                  body > div:first-child,
                  main,
                  .content-wrapper,
                  .container,
                  .blog-container,
                  .article-container {
                    background-color: transparent !important;
                  }
                `;
                doc.head?.appendChild(styleEl);

                // Any link in the admin HTML that this file does not explicitly intercept below
                // would otherwise navigate THIS IFRAME (a srcdoc document inherits the parent's
                // base URL, so even a same-site href resolves and loads the React app inside the
                // article box -> duplicate header/footer). `target="_top"` makes every such
                // navigation land on the top window instead, which is the only place a page
                // navigation ever makes sense here. Belt-and-braces with the explicit click
                // handlers further down, which still run first and preventDefault.
                const baseEl = doc.createElement("base");
                baseEl.target = "_top";
                doc.head?.appendChild(baseEl);

                if (doc.documentElement) {
                  doc.documentElement.style.background = "transparent";
                  doc.documentElement.style.backgroundColor = "transparent";
                  doc.documentElement.style.overflow = "hidden";
                }
                if (doc.body) {
                  doc.body.style.background = "transparent";
                  doc.body.style.backgroundColor = "transparent";
                  doc.body.style.overflow = "hidden";

                  // Remove opaque full-width background on root containers if present
                  Array.from(doc.body.children).forEach((child) => {
                    const htmlChild = child as HTMLElement;
                    const bg = window.getComputedStyle(htmlChild).backgroundColor;
                    if (
                      bg === "rgb(255, 255, 255)" ||
                      bg === "rgba(255, 255, 255, 1)"
                    ) {
                      if (htmlChild.offsetWidth >= (doc.body?.offsetWidth || 0) * 0.85) {
                        htmlChild.style.backgroundColor = "transparent";
                      }
                    }
                  });
                }

                // 2. Dynamic height adjustment to avoid vertical scrollbar
                const updateHeight = () => {
                  try {
                    const h = Math.max(
                      doc.body?.scrollHeight || 0,
                      doc.documentElement?.scrollHeight || 0,
                      doc.body?.offsetHeight || 0,
                      doc.documentElement?.offsetHeight || 0
                    );
                    if (h > 0) {
                      setApiFrameReady(true);
                      frame.style.height = `${h + 10}px`;
                    }
                  } catch {
                    // Ignore cross-origin issues
                  }
                };

                updateHeight();

                if (window.ResizeObserver && doc.body) {
                  const ro = new ResizeObserver(() => {
                    updateHeight();
                  });
                  ro.observe(doc.body);
                }

                doc.querySelectorAll("img").forEach((img) => {
                  if (!img.complete) {
                    img.addEventListener("load", updateHeight);
                    img.addEventListener("error", updateHeight);
                  }
                });

                if (doc.fonts && doc.fonts.ready) {
                  doc.fonts.ready.then(updateHeight);
                }

                // 3a. In-page anchor links from the admin HTML's own "On this page" TOC
                // (href="#introduction" etc.).
                //
                // This iframe is `srcDoc`, so its document URL is `about:srcdoc` and it INHERITS
                // the parent's base URL. That means a bare fragment does NOT stay in-document the
                // way it would in a normal page -- the browser resolves "#introduction" against
                // the PARENT url and navigates THIS IFRAME to /blogs/<slug>#introduction, which
                // loads the whole React app inside the article box and renders the duplicate
                // header/footer. (App.tsx's iframe-breakout guard is the last-resort net for that;
                // this stops it from ever happening.)
                //
                // Deliberately `^="#"` but excluding the bare "#" handled below: "#" is the blog's
                // CTA button (no target, routes to the app/store), "#something" is a real section
                // link. The article renders at full height inside a non-scrolling iframe, so the
                // section is scrolled to on the PARENT window, offset by the iframe's own position
                // plus room for the sticky site header.
                const STICKY_HEADER_OFFSET = 110;
                doc.querySelectorAll('a[href^="#"]:not([href="#"])').forEach((a) => {
                  a.addEventListener("click", (evt) => {
                    const rawHref = a.getAttribute("href") || "";
                    const id = decodeURIComponent(rawHref.slice(1));
                    if (!id) return;
                    // Fall back to `[name]` for older admin HTML that predates id-based anchors.
                    const target =
                      doc.getElementById(id) ||
                      doc.querySelector(`[name="${CSS.escape(id)}"]`);
                    if (!target) return;
                    evt.preventDefault();
                    // This layout puts `overflow-y: auto` on BOTH html and body, which makes
                    // <body> the element that actually scrolls -- while `document.scrollingElement`
                    // still reports documentElement. So `window.scrollTo()` / `documentElement
                    // .scrollTop` are silent no-ops here (verified live: body.scrollTop = 500
                    // worked, the other two stayed at 0). Pick the node that genuinely scrolls
                    // instead of trusting either default.
                    const scroller =
                      [document.body, document.documentElement].find(
                        (el) =>
                          el &&
                          el.scrollHeight > el.clientHeight + 1 &&
                          ["auto", "scroll", "overlay"].includes(
                            window.getComputedStyle(el).overflowY
                          )
                      ) ||
                      document.scrollingElement ||
                      document.documentElement;

                    // The iframe is unscrollable and laid out at full article height, so a target
                    // inside it sits at (iframe's offset in the scroller) + (target's offset in
                    // the iframe). Verified live: this lands every heading at exactly
                    // STICKY_HEADER_OFFSET from the top of the viewport.
                    const top =
                      frame.getBoundingClientRect().top +
                      scroller.scrollTop +
                      target.getBoundingClientRect().top -
                      STICKY_HEADER_OFFSET;
                    const destination = Math.max(0, top);
                    const startTop = scroller.scrollTop;
                    try {
                      scroller.scrollTo({ top: destination, behavior: "smooth" });
                    } catch {
                      // Engines without the options-object overload throw on it outright.
                      scroller.scrollTop = destination;
                    }
                    // Smooth scrolling is not reliably honoured on a <body> scroller -- some
                    // engines (older Safari especially) silently ignore the options object and
                    // simply do nothing, which reads to the user as "the link is dead" (verified
                    // live here: behavior:"smooth" left scrollTop at 0 while behavior:"auto" and
                    // a direct assignment both moved it). If nothing has budged at all shortly
                    // after, jump straight there -- an instant scroll beats no scroll.
                    window.setTimeout(() => {
                      if (scroller.scrollTop === startTop && destination !== startTop) {
                        scroller.scrollTop = destination;
                      }
                    }, 250);
                  });
                });

                // 3b. Blog CTA buttons routing
                doc.querySelectorAll('a[href="#"]').forEach((a) => {
                  a.addEventListener("click", (evt) => {
                    evt.preventDefault();
                    const ua = navigator.userAgent;
                    if (/iPad|iPhone|iPod/.test(ua)) {
                      window.open(
                        "https://apps.apple.com/us/app/ezy-watermark-photos-lite/id494473910",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    } else if (/Android/.test(ua)) {
                      window.open(
                        "https://play.google.com/store/apps/details?id=com.whizpool.ezywatermarklite",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    } else {
                      history.push("/home");
                    }
                  });
                });
              } catch {
                // Cross-origin or otherwise inaccessible -- leave height as-is.
                setApiFrameReady(true);
              }
            }}
          />
        </section>
      </div>
    );
  }

  if (ghostPost === undefined && !staticPost) {
    // Still resolving the CMS lookup -- avoid a flash of "not found" while the (empty) API
    // call is in flight, for a slug that has no matching static post either.
    return <BlogDetailSkeleton />;
  }

  if (ghostPost) {
    return (
      <div className="landing-redesign">
        <Nav />
        <ProgressBar pct={pct} />
        <section className="bd-hero lr-grain">
          <div className="bd-hero__content">
            <button type="button" className="bd-hero__back" onClick={() => history.push("/blogs")}>
              ← Back to blogs
            </button>
            <h1 className="bd-hero__title">{ghostPost.title}</h1>
            {ghostPost.published_at && (
              <div className="bd-hero__date">{moment(ghostPost.published_at).format("MMMM D, YYYY")}</div>
            )}
          </div>
          {ghostPost.feature_image && (
            <div className="bd-hero__media">
              <img src={ghostPost.feature_image} alt="" />
            </div>
          )}
        </section>
        <section className="bd-article">
          <div
            className="bd-article__body"
            dangerouslySetInnerHTML={{ __html: ghostPost.html }}
          />
        </section>
      </div>
    );
  }

  if (staticPost) {
    return <StaticPostView post={staticPost} pct={pct} onBack={() => history.push("/blogs")} />;
  }

  return <NotFound onBack={() => history.push("/blogs")} />;
};

const ProgressBar: React.FC<{ pct: number }> = ({ pct }) => (
  <div className="bd-progress">
    <div className="bd-progress__bar" style={{ width: `${pct}%` }} />
  </div>
);

// Nav + hero + article shell shown from the very first paint whenever real content is not yet
// known -- used both here (still resolving which source has this post) and while the Blogs API
// iframe below is loading. Without this, the initial fetch phase rendered a bare empty <div>,
// so the header disappeared and the page was just the global Footer for a moment before this
// component had anything to show.
const BlogDetailSkeleton: React.FC = () => (
  <div className="landing-redesign">
    <Nav />
    <section className="bd-hero bd-hero--compact lr-grain">
      <div className="bd-hero__orbs">
        <div className="bd-hero__orb bd-hero__orb--1" />
        <div className="bd-hero__orb bd-hero__orb--2" />
      </div>
      <div className="bd-hero__content">
        <span className="bd-hero__badge">
          <span />
          The eZy Journal
        </span>
      </div>
    </section>
    <section className="bd-article bd-article--api">
      <div className="bd-article__api-skeleton" aria-hidden="true">
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "60%", height: "1.6em" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "95%", marginTop: "1.25em" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "90%" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "97%" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "40%" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "85%", marginTop: "1.25em" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "92%" }} />
        <div className="bd-skeleton-shimmer bd-skeleton-line" style={{ width: "60%" }} />
      </div>
    </section>
  </div>
);

const StaticPostView: React.FC<{ post: BlogPost; pct: number; onBack: () => void }> = ({
  post,
  pct,
  onBack,
}) => {
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const fallbackRelated = related.length > 0 ? related : BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const body = post.body ?? [{ paragraphs: [post.excerpt] }];

  return (
    <div className="landing-redesign">
      <Nav />
      <ProgressBar pct={pct} />

      <section className="bd-hero lr-grain">
        <div className="bd-hero__orbs">
          <div className="bd-hero__orb bd-hero__orb--1" />
          <div className="bd-hero__orb bd-hero__orb--2" />
        </div>
        <div className="bd-hero__content">
          <div className="bd-hero__crumbs">
            <button type="button" className="bd-hero__back" onClick={onBack}>
              ← Back to blogs
            </button>
            <span>/</span>
            <span className="bd-hero__category">{post.category}</span>
          </div>
          <h1 className="bd-hero__title">{post.title}</h1>
          <p className="bd-hero__excerpt">{post.excerpt}</p>
          <div className="bd-hero__byline">
            <span className="bd-hero__avatar">{post.initials}</span>
            <div>
              <div className="bd-hero__author">{post.author}</div>
              <div className="bd-hero__meta">{post.date} · {post.readMin} min read</div>
            </div>
          </div>
        </div>
        <div className="bd-hero__media">
          <img src={post.cover} alt="" />
          <span className="bd-hero__media-category">{post.category}</span>
          <span className="bd-hero__media-stamp">© eZy Watermark</span>
        </div>
      </section>

      <section className="bd-article">
        <div className="bd-article__body">
          {body.map((section, i) => (
            <div key={i} className="bd-article__section">
              {section.heading && (
                <h2>
                  <span aria-hidden="true" />
                  {section.heading}
                </h2>
              )}
              {section.paragraphs.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
          ))}

          <blockquote className="bd-article__quote">
            <p>&quot;A watermark should feel like a signature at the bottom of a painting — quiet, confident, unmistakably yours.&quot;</p>
            <footer>— {post.author}</footer>
          </blockquote>

          {post.tags && post.tags.length > 0 && (
            <div className="bd-article__tags">
              <span>Tags</span>
              {post.tags.map((t) => (
                <span key={t}>#{t}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bd-author">
        <div className="bd-author__card">
          <span className="bd-author__avatar">{post.initials}</span>
          <div>
            <div className="bd-author__label">Written by</div>
            <div className="bd-author__name">{post.author}</div>
            {post.authorBio && <p className="bd-author__bio">{post.authorBio}</p>}
          </div>
        </div>
      </section>

      {fallbackRelated.length > 0 && (
        <section className="bd-related">
          <div className="bd-related__head">
            <h3>Keep reading</h3>
            <button type="button" onClick={onBack}>All articles →</button>
          </div>
          <div className="bd-related__grid">
            {fallbackRelated.map((p) => (
              <a key={p.slug} className="bd-related__card" href={`/blogs/${p.slug}`}>
                <div className="bd-related__media">
                  <img src={p.cover} alt="" />
                  <span>{p.category}</span>
                </div>
                <div className="bd-related__body">
                  <div className="bd-related__meta">{p.date} · {p.readMin} min</div>
                  <h4>{p.title}</h4>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const NotFound: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="landing-redesign">
    <Nav />
    <section className="bd-notfound">
      <span>404</span>
      <h1>Article not found</h1>
      <p>This story may have moved. Head back to the journal to find something new.</p>
      <button type="button" onClick={onBack}>
        Browse all blogs →
      </button>
    </section>
  </div>
);

export default BlogDetails;
